import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Modal } from "antd";
import Sidebar from "../../GlobalFiles/Sidebar";
import { API_BASE_URL } from "../../../../../api";
import {
  RefreshCcwDot,
  Eye,
  EyeOff,
  Loader2,
  Key,
} from "lucide-react";

const notify = (text, options) => {
  const opts = typeof options === "object" ? options : {};
  const toastId = opts.toastId || opts.id || text;
  toast(text, { toastId, ...opts });
};
const SUPER_ADMIN_STORAGE_KEY = "superAdminAuth";
const SUPER_ADMIN_TTL_MS = 24 * 60 * 60 * 1000;
const ACTIVITY_PAGE_SIZE = 30;

const SuperAdmin = () => {
  const { data } = useSelector((store) => store.auth);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [managers, setManagers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [changePasswordTarget, setChangePasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityFilters, setActivityFilters] = useState({
    actorType: "",
    targetType: "",
    action: "",
  });
  const [activeTab, setActiveTab] = useState("admins");
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [editingManagerId, setEditingManagerId] = useState(null);
  const [adminForm, setAdminForm] = useState({
    adminName: "",
    email: "",
    mobile: "",
    password: "",
  });
  const [managerForm, setManagerForm] = useState({
    managerName: "",
    email: "",
    mobile: "",
    password: "",
  });
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [showAccessPassword, setShowAccessPassword] = useState(false);

  const headers = useMemo(() => ({ "x-super-admin-password": password }), [password]);

  const getErrorMessage = useCallback((error, fallback) => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
    );
  }, []);

  const handleUnauthorized = useCallback((error) => {
    if (error?.response?.status === 401) {
      notify(getErrorMessage(error, "Invalid super admin password"));
      localStorage.removeItem(SUPER_ADMIN_STORAGE_KEY);
      setUnlocked(false);
      return true;
    }
    return false;
  }, [getErrorMessage]);

  const loadData = useCallback(async () => {
    if (!password) {
      return;
    }

    setLoading(true);
    try {
      const [adminsRes, managersRes, activitiesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/super-admin/admins`, { headers }),
        axios.get(`${API_BASE_URL}/super-admin/managers`, { headers }),
        axios.get(`${API_BASE_URL}/super-admin/activities`, {
          headers,
          params: {
            page: activityPage,
            limit: ACTIVITY_PAGE_SIZE,
            ...activityFilters,
          },
        }),
      ]);

      setAdmins(adminsRes.data || []);
      setManagers(managersRes.data || []);
      setActivities(activitiesRes.data?.data || []);
      setActivityTotalPages(activitiesRes.data?.totalPages || 1);
      setActivityTotal(activitiesRes.data?.total || 0);
    } catch (error) {
      if (!handleUnauthorized(error)) {
        notify(getErrorMessage(error, "Failed to load super admin data"));
      }
    } finally {
      setLoading(false);
    }
  }, [activityFilters, activityPage, getErrorMessage, headers, handleUnauthorized, password]);

  useEffect(() => {
    if (unlocked) {
      loadData();
    }
  }, [loadData, unlocked]);

  useEffect(() => {
    const cached = localStorage.getItem(SUPER_ADMIN_STORAGE_KEY);
    if (!cached) {
      return;
    }

    try {
      const parsed = JSON.parse(cached);
      const cachedPassword = parsed?.password;
      const savedAt = Number(parsed?.savedAt);
      if (!cachedPassword || Number.isNaN(savedAt)) {
        localStorage.removeItem(SUPER_ADMIN_STORAGE_KEY);
        return;
      }
      if (Date.now() - savedAt > SUPER_ADMIN_TTL_MS) {
        localStorage.removeItem(SUPER_ADMIN_STORAGE_KEY);
        return;
      }

      setPassword(cachedPassword);
      setUnlocked(true);
    } catch (_error) {
      localStorage.removeItem(SUPER_ADMIN_STORAGE_KEY);
    }
  }, []);

  // if (data?.isAuthenticated === false) {
  //   return <Navigate to="/" />;
  // }

  const handleUnlock = async (event) => {
    event.preventDefault();

    if (!password.trim()) {
      notify("Enter the super admin password");
      return;
    }

    try {
      setUnlockLoading(true);

      const unlockHeaders = {
        "x-super-admin-password": password,
      };

      const [adminsRes, managersRes, activitiesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/super-admin/admins`, {
          headers: unlockHeaders,
        }),
        axios.get(`${API_BASE_URL}/super-admin/managers`, {
          headers: unlockHeaders,
        }),
        axios.get(`${API_BASE_URL}/super-admin/activities`, {
          headers: unlockHeaders,
          params: {
            page: activityPage,
            limit: ACTIVITY_PAGE_SIZE,
            ...activityFilters,
          },
        }),
      ]);

      setAdmins(adminsRes.data || []);
      setManagers(managersRes.data || []);
      setActivities(activitiesRes.data?.data || []);
      setActivityTotalPages(activitiesRes.data?.totalPages || 1);
      setActivityTotal(activitiesRes.data?.total || 0);

      setUnlocked(true);

      localStorage.setItem(
        SUPER_ADMIN_STORAGE_KEY,
        JSON.stringify({
          password,
          savedAt: Date.now(),
        })
      );
    } catch (error) {
      localStorage.removeItem(SUPER_ADMIN_STORAGE_KEY);
      setUnlocked(false);

      notify(
        getErrorMessage(error, "Invalid super admin password")
      );
    } finally {
      setUnlockLoading(false);
    }
  };

  const openChangePasswordModal = (user, type) => {
    setChangePasswordTarget({
      id: user._id,
      name: type === "admin" ? user.adminName : user.managerName,
      email: user.email,
      type,
    });
    setNewPassword("");
    setShowNewPassword(false);
    setIsChangePasswordModalOpen(true);
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim()) {
      notify("Please enter a new password");
      return;
    }
    if (!changePasswordTarget) return;

    const { id, type } = changePasswordTarget;
    setChangePasswordLoading(true);
    try {
      if (type === "admin") {
        await axios.patch(
          `${API_BASE_URL}/super-admin/admins/${id}/password`,
          { password: newPassword },
          { headers }
        );
        notify("Admin password updated and emailed");
      } else {
        await axios.patch(
          `${API_BASE_URL}/super-admin/managers/${id}/password`,
          { password: newPassword },
          { headers }
        );
        notify("Manager password updated and emailed");
      }
      setIsChangePasswordModalOpen(false);
    } catch (error) {
      if (!handleUnauthorized(error)) {
        notify(getErrorMessage(error, `Failed to update ${type} password`));
      }
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const deleteAdmin = async (adminId) => {
    try {
      await axios.delete(`${API_BASE_URL}/super-admin/admins/${adminId}`, { headers });
      notify("Admin deleted");
      setAdmins((prev) => prev.filter((admin) => admin._id !== adminId));
    } catch (error) {
      if (!handleUnauthorized(error)) {
        notify(getErrorMessage(error, "Failed to delete admin"));
      }
    }
  };

  const deleteManager = async (managerId) => {
    try {
      await axios.delete(`${API_BASE_URL}/super-admin/managers/${managerId}`, { headers });
      notify("Manager deleted");
      setManagers((prev) => prev.filter((manager) => manager._id !== managerId));
    } catch (error) {
      if (!handleUnauthorized(error)) {
        notify(getErrorMessage(error, "Failed to delete manager"));
      }
    }
  };

  const deleteActivity = async (activityId) => {
    try {
      await axios.delete(`${API_BASE_URL}/super-admin/activities/${activityId}`, { headers });
      notify("Activity deleted", { id: "activity-deleted" });
      setActivities((prev) => prev.filter((activity) => activity._id !== activityId));
    } catch (error) {
      if (!handleUnauthorized(error)) {
        notify(getErrorMessage(error, "Failed to delete activity"));
      }
    }
  };

  const openAdminCreate = () => {
    setEditingAdminId(null);
    setAdminForm({ adminName: "", email: "", mobile: "", password: "" });
    setIsAdminModalOpen(true);
  };

  const openAdminEdit = (admin) => {
    setEditingAdminId(admin._id);
    setAdminForm({
      adminName: admin.adminName || "",
      email: admin.email || "",
      mobile: admin.mobile || "",
      password: "",
    });
    setIsAdminModalOpen(true);
  };

  const openManagerCreate = () => {
    setEditingManagerId(null);
    setManagerForm({ managerName: "", email: "", mobile: "", password: "" });
    setIsManagerModalOpen(true);
  };

  const openManagerEdit = (manager) => {
    setEditingManagerId(manager._id);
    setManagerForm({
      managerName: manager.managerName || "",
      email: manager.email || "",
      mobile: manager.mobile || "",
      password: "",
    });
    setIsManagerModalOpen(true);
  };

  const saveAdmin = async (event) => {
    event.preventDefault();
    if (!adminForm.adminName || !adminForm.email || (!editingAdminId && !adminForm.password)) {
      notify("Admin name, email, and password are required for new admins");
      return;
    }

    try {
      if (editingAdminId) {
        const payload = { ...adminForm };
        delete payload.password;
        const response = await axios.patch(
          `${API_BASE_URL}/super-admin/admins/${editingAdminId}`,
          payload,
          { headers }
        );
        setAdmins((prev) => prev.map((admin) => (admin._id === editingAdminId ? response.data.admin : admin)));
        notify("Admin updated");
      } else {
        const response = await axios.post(`${API_BASE_URL}/super-admin/admins`, adminForm, { headers });
        setAdmins((prev) => [response.data.admin, ...prev]);
        notify("Admin created");
      }
      setIsAdminModalOpen(false);
    } catch (error) {
      if (!handleUnauthorized(error)) {
        notify(getErrorMessage(error, "Failed to save admin"));
      }
    }
  };

  const saveManager = async (event) => {
    event.preventDefault();
    if (!managerForm.managerName || !managerForm.email || (!editingManagerId && !managerForm.password)) {
      notify("Manager name, email, and password are required for new managers");
      return;
    }

    try {
      if (editingManagerId) {
        const payload = { ...managerForm };
        delete payload.password;
        const response = await axios.patch(
          `${API_BASE_URL}/super-admin/managers/${editingManagerId}`,
          payload,
          { headers }
        );
        setManagers((prev) => prev.map((manager) => (manager._id === editingManagerId ? response.data.manager : manager)));
        notify("Manager updated");
      } else {
        const response = await axios.post(`${API_BASE_URL}/super-admin/managers`, managerForm, { headers });
        setManagers((prev) => [response.data.manager, ...prev]);
        notify("Manager created");
      }
      setIsManagerModalOpen(false);
    } catch (error) {
      if (!handleUnauthorized(error)) {
        notify(getErrorMessage(error, "Failed to save manager"));
      }
    }
  };

  const activityStart = activityTotal === 0 ? 0 : (activityPage - 1) * ACTIVITY_PAGE_SIZE + 1;
  const activityEnd = Math.min(activityPage * ACTIVITY_PAGE_SIZE, activityTotal);

  return (
    <>
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar onCollapse={setSidebarCollapsed} />
        <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? "ml-20" : "ml-0"}`}>
          <div className="max-w-6xl mx-auto space-y-4">
            {!unlocked ? (
              <div className="max-w-md mx-auto my-12 bg-white p-8 rounded-xl shadow-md border border-gray-200">
                <div className="text-center mb-6">
                  <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                    <Key className="w-6 h-6 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Super Admin Access</h2>
                  <p className="text-sm text-gray-500 mt-1">Please enter your super admin password to access this page.</p>
                </div>
                <form onSubmit={handleUnlock} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showAccessPassword ? "text" : "password"}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="Enter super admin password"
                        disabled={unlockLoading}
                        className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />

                      <button
                        type="button"
                        onClick={() =>
                          setShowAccessPassword((prev) => !prev)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showAccessPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={unlockLoading}
                    className="w-full px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-colors"
                  >
                    {unlockLoading && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}

                    {unlockLoading ? "Checking Access..." : "Unlock Access"}
                  </button>
                  <button
                    type="button"
                    disabled={unlockLoading}
                    className="w-full px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 disabled:opacity-60 font-medium transition-colors"
                    onClick={() => {
                      window.history.back();
                    }}
                  >
                    Cancel
                  </button>
                </form>
              </div>
            ) : (
              <>
                <Modal
                  title={editingAdminId ? "Edit Admin" : "Add Admin"}
                  open={isAdminModalOpen}
                  onCancel={() => setIsAdminModalOpen(false)}
                  footer={null}
                  centered
                >
                  <form onSubmit={saveAdmin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={adminForm.adminName}
                        onChange={(event) => setAdminForm((prev) => ({ ...prev, adminName: event.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={adminForm.email}
                        onChange={(event) => setAdminForm((prev) => ({ ...prev, email: event.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                      <input
                        type="text"
                        value={adminForm.mobile}
                        onChange={(event) => setAdminForm((prev) => ({ ...prev, mobile: event.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    {!editingAdminId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <input
                          type="text"
                          value={adminForm.password}
                          onChange={(event) => setAdminForm((prev) => ({ ...prev, password: event.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                      </div>
                    )}
                    <button
                      type="submit"
                      className="w-full px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {editingAdminId ? "Update Admin" : "Create Admin"}
                    </button>
                  </form>
                </Modal>
                <Modal
                  title={editingManagerId ? "Edit Manager" : "Add Manager"}
                  open={isManagerModalOpen}
                  onCancel={() => setIsManagerModalOpen(false)}
                  footer={null}
                  centered
                >
                  <form onSubmit={saveManager} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={managerForm.managerName}
                        onChange={(event) => setManagerForm((prev) => ({ ...prev, managerName: event.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={managerForm.email}
                        onChange={(event) => setManagerForm((prev) => ({ ...prev, email: event.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                      <input
                        type="text"
                        value={managerForm.mobile}
                        onChange={(event) => setManagerForm((prev) => ({ ...prev, mobile: event.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    {!editingManagerId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <input
                          type="text"
                          value={managerForm.password}
                          onChange={(event) => setManagerForm((prev) => ({ ...prev, password: event.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                      </div>
                    )}
                    <button
                      type="submit"
                      className="w-full px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {editingManagerId ? "Update Manager" : "Create Manager"}
                    </button>
                  </form>
                </Modal>

                <Modal
                  title="Change Password"
                  open={isChangePasswordModalOpen}
                  onCancel={() => setIsChangePasswordModalOpen(false)}
                  footer={null}
                  centered
                >
                  {changePasswordTarget && (
                    <form onSubmit={handleSavePassword} className="space-y-4">
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Target User
                        </p>
                        <p className="font-bold text-gray-900 text-base">
                          {changePasswordTarget.name}
                        </p>
                        <p className="text-xs text-gray-600">
                          {changePasswordTarget.email} ({changePasswordTarget.type})
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            disabled={changePasswordLoading}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword((prev) => !prev)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          The updated credentials will be emailed to the user automatically.
                        </p>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => setIsChangePasswordModalOpen(false)}
                          className="w-1/2 px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm font-medium border border-gray-300"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={changePasswordLoading}
                          className="w-1/2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 text-sm font-medium flex items-center justify-center gap-2"
                        >
                          {changePasswordLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                          {changePasswordLoading ? "Updating..." : "Update Password"}
                        </button>
                      </div>
                    </form>
                  )}
                </Modal>
                <div className="bg-white rounded-lg shadow p-6 flex flex-row w-full items-center justify-between">
                  <h1 className="text-3xl font-bold text-gray-900">Super Admin</h1>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      type="button"
                      className="px-5 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 flex flex-row gap-2 items-center"
                      disabled={loading}
                      onClick={loadData}
                    >
                      <RefreshCcwDot className={`${loading ? 'animate-spin' : ''} h-5 w-5`} />
                      {loading ? "Refreshing..." : "Refresh Data"}
                    </button>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setActiveTab("admins")}
                        className={`px-4 py-2 rounded-md border ${activeTab === "admins"
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                          }`}
                      >
                        Admins
                      </button>
                      <button
                        onClick={() => setActiveTab("managers")}
                        className={`px-4 py-2 rounded-md border ${activeTab === "managers"
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                          }`}
                      >
                        Managers
                      </button>
                      <button
                        onClick={() => setActiveTab("activities")}
                        className={`px-4 py-2 rounded-md border ${activeTab === "activities"
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                          }`}
                      >
                        Activities
                      </button>
                    </div>
                  </div>

                  {activeTab === "admins" && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-semibold text-gray-900">Admins</h2>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-500">{admins.length} total</span>
                          <button
                            onClick={openAdminCreate}
                            className="px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                          >
                            Add Admin
                          </button>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-left">
                          <thead className="bg-gray-100 text-gray-700">
                            <tr>
                              <th className="px-4 py-3 font-semibold">Admin ID</th>
                              <th className="px-4 py-3 font-semibold">Name</th>
                              <th className="px-4 py-3 font-semibold">Email</th>
                              <th className="px-4 py-3 font-semibold">Mobile</th>
                              <th className="px-4 py-3 font-semibold text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {admins.map((admin) => (
                              <tr key={admin._id} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-3 font-mono text-gray-800">{admin.adminID}</td>
                                <td className="px-4 py-3 font-medium text-gray-900">{admin.adminName}</td>
                                <td className="px-4 py-3 text-gray-600">{admin.email}</td>
                                <td className="px-4 py-3 text-gray-600">{admin.mobile || "N/A"}</td>
                                <td className="px-4 py-3 space-x-2 text-right">
                                  <button
                                    onClick={() => openAdminEdit(admin)}
                                    className="px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium text-xs border border-gray-300 transition"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => openChangePasswordModal(admin, "admin")}
                                    className="px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium text-xs border border-blue-200 inline-flex items-center gap-1 transition"
                                  >
                                    <Key className="w-3.5 h-3.5" />
                                    Change Password
                                  </button>
                                  <button
                                    onClick={() => deleteAdmin(admin._id)}
                                    className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100 font-medium text-xs border border-red-200 transition"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {admins.length === 0 && (
                              <tr>
                                <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                                  No admins found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {activeTab === "managers" && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-semibold text-gray-900">Managers</h2>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-500">{managers.length} total</span>
                          <button
                            onClick={openManagerCreate}
                            className="px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-medium text-sm"
                          >
                            Add Manager
                          </button>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-left">
                          <thead className="bg-gray-100 text-gray-700">
                            <tr>
                              <th className="px-4 py-3 font-semibold">Manager ID</th>
                              <th className="px-4 py-3 font-semibold">Name</th>
                              <th className="px-4 py-3 font-semibold">Email</th>
                              <th className="px-4 py-3 font-semibold">Mobile</th>
                              <th className="px-4 py-3 font-semibold text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {managers.map((manager) => (
                              <tr key={manager._id} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-3 font-mono text-gray-800">{manager.managerID}</td>
                                <td className="px-4 py-3 font-medium text-gray-900">{manager.managerName}</td>
                                <td className="px-4 py-3 text-gray-600">{manager.email}</td>
                                <td className="px-4 py-3 text-gray-600">{manager.mobile || "N/A"}</td>
                                <td className="px-4 py-3 space-x-2 text-right">
                                  <button
                                    onClick={() => openManagerEdit(manager)}
                                    className="px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium text-xs border border-gray-300 transition"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => openChangePasswordModal(manager, "manager")}
                                    className="px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium text-xs border border-blue-200 inline-flex items-center gap-1 transition"
                                  >
                                    <Key className="w-3.5 h-3.5" />
                                    Change Password
                                  </button>
                                  <button
                                    onClick={() => deleteManager(manager._id)}
                                    className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100 font-medium text-xs border border-red-200 transition"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {managers.length === 0 && (
                              <tr>
                                <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                                  No managers found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {activeTab === "activities" && (
                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-semibold text-gray-900">Activity Log</h2>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">
                            Showing {activityStart}-{activityEnd} of {activityTotal}
                          </span>
                          <button
                            onClick={() => setActivityPage((prev) => Math.max(prev - 1, 1))}
                            className="px-3 py-1 rounded border border-gray-300 text-gray-700"
                            disabled={activityPage === 1}
                          >
                            Prev
                          </button>
                          <span className="text-sm text-gray-500">Page {activityPage} of {activityTotalPages}</span>
                          <button
                            onClick={() => setActivityPage((prev) => Math.min(prev + 1, activityTotalPages))}
                            className="px-3 py-1 rounded border border-gray-300 text-gray-700"
                            disabled={activityPage >= activityTotalPages}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <select
                          value={activityFilters.actorType}
                          onChange={(event) => {
                            setActivityFilters((prev) => ({ ...prev, actorType: event.target.value }));
                            setActivityPage(1);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">All Actors</option>
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                          <option value="doctor">Doctor</option>
                          <option value="agent">Agent</option>
                          <option value="patient">Patient</option>
                          <option value="super-admin">Super Admin</option>
                          <option value="system">System</option>
                        </select>
                        <select
                          value={activityFilters.targetType}
                          onChange={(event) => {
                            setActivityFilters((prev) => ({ ...prev, targetType: event.target.value }));
                            setActivityPage(1);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">All Targets</option>
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                          <option value="doctor">Doctor</option>
                          <option value="agent">Agent</option>
                          <option value="patient">Patient</option>
                          <option value="appointment">Appointment</option>
                          <option value="report">Report</option>
                          <option value="test-order">Test Order</option>
                          <option value="test">Test</option>
                          <option value="test-category">Test Category</option>
                          <option value="doctor-payment">Doctor Payment</option>
                          <option value="agent-payment">Agent Payment</option>
                          <option value="text-info">Text Info</option>
                          <option value="sync">Sync</option>
                        </select>
                        <select
                          value={activityFilters.action}
                          onChange={(event) => {
                            setActivityFilters((prev) => ({ ...prev, action: event.target.value }));
                            setActivityPage(1);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">All Actions</option>
                          <option value="create">Create</option>
                          <option value="update">Update</option>
                          <option value="delete">Delete</option>
                          <option value="password-change">Password Change</option>
                        </select>
                      </div>
                      <div className="space-y-3">
                        {activities.map((activity) => {
                          let icon = (
                            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                          );
                          let bgColor = "bg-gray-100";
                          let iconColor = "text-gray-600";
                          let actionText = activity.action;

                          if (activity.action === "create") {
                            icon = <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>;
                            bgColor = "bg-green-100";
                            iconColor = "text-green-600";
                            actionText = "created a new";
                          } else if (activity.action === "update") {
                            icon = <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>;
                            bgColor = "bg-blue-100";
                            iconColor = "text-blue-600";
                            actionText = "updated the";
                          } else if (activity.action === "delete") {
                            icon = <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>;
                            bgColor = "bg-red-100";
                            iconColor = "text-red-600";
                            actionText = "deleted a";
                          } else if (activity.action === "password-change") {
                            icon = <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path></svg>;
                            bgColor = "bg-purple-100";
                            iconColor = "text-purple-600";
                            actionText = "changed password for";
                          }

                          const getTargetIdentifier = (act) => {
                            const c = act.changes || {};
                            const possibleIds = [c.patientID, c.docID, c.agentID, c.adminID, c.managerID, c.testOrderID, c.reportID, c.categoryId, c.testId];
                            const foundId = possibleIds.find(val => val !== undefined && val !== null && val !== "");

                            const possibleNames = [c.patientName, c.docName, c.name, c.adminName, c.managerName, c.title, c.categoryName];
                            const foundName = possibleNames.find(val => val !== undefined && val !== null && val !== "");

                            if (act.targetType === "test-order" || c.testOrderID) {
                              const orderNum = c.testOrderID || (foundId && !foundName ? foundId : null);
                              if (foundName && orderNum) return `${foundName} (Order #${orderNum})`;
                              if (foundName) return `${foundName}`;
                              if (orderNum) return `Order #${orderNum}`;
                            }

                            if (foundId && foundName) return `${foundName} (ID: ${foundId})`;
                            if (foundName) return foundName;
                            if (foundId) return `ID: ${foundId}`;

                            if (act.targetId) {
                              if (act.targetId.length === 24 && /^[0-9a-fA-F]{24}$/.test(act.targetId)) {
                                return `ID: #${act.targetId.slice(-6)}`;
                              }
                              return act.targetId;
                            }
                            return "N/A";
                          };

                          const targetIdent = getTargetIdentifier(activity);

                          return (
                            <div
                              key={activity._id}
                              className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-md hover:border-blue-300 transition"
                              onClick={() => setSelectedActivity({ ...activity, icon, bgColor, iconColor, actionText, targetIdent })}
                            >
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-3">
                                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${bgColor} ${iconColor} shrink-0`}>
                                    {icon}
                                  </div>
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-gray-900 text-base">{activity.actorName || "System"}</span>
                                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 uppercase tracking-wider">{activity.actorType}</span>
                                    </div>
                                    <time className="text-xs text-gray-500 font-medium mt-0.5">
                                      {new Date(activity.createdAt).toLocaleDateString()} {new Date(activity.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </time>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteActivity(activity._id);
                                  }}
                                  className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100 transition border border-red-100"
                                >
                                  Delete
                                </button>
                              </div>

                              <div className="mt-3 bg-gray-50 rounded p-2.5 border border-gray-100">
                                <p className="text-gray-700 text-sm flex flex-wrap items-center gap-1.5">
                                  <span className="text-gray-600">{actionText}</span>
                                  <span className="font-bold text-gray-900 capitalize">{activity.targetType.replace(/-/g, " ")}</span>
                                  <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200 text-gray-800 shadow-sm">{targetIdent}</span>
                                </p>
                              </div>

                              {activity.message && activity.message !== `${activity.targetType} ${activity.action}d` && (
                                <p className="text-xs text-gray-500 italic mt-3 truncate border-l-2 border-gray-200 pl-2">&quot;{activity.message}&quot;</p>
                              )}
                            </div>
                          );
                        })}
                        {activities.length === 0 && (
                          <div className="text-center text-gray-500">No activities yet.</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Activity Details Modal */}
                  {selectedActivity && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">

                        {/* Modal Header */}
                        <div className={`flex justify-between items-center p-4 border-b ${selectedActivity.bgColor}`}>
                          <div className="flex items-center">
                            <div className={`w-8 h-8 rounded-full bg-white flex items-center justify-center mr-3 ${selectedActivity.iconColor}`}>
                              {selectedActivity.icon}
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 capitalize">{selectedActivity.actionText} {selectedActivity.targetType}</h3>
                          </div>
                          <button onClick={() => setSelectedActivity(null)} className="text-gray-500 hover:text-gray-800 bg-white/50 hover:bg-white rounded-full p-1 transition">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                          </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto">
                          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                            <div>
                              <p className="text-gray-500 mb-1 font-medium">Actor</p>
                              <p className="font-semibold text-gray-900 flex items-center gap-2">
                                {selectedActivity.actorName || "System"}
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 uppercase tracking-wider">{selectedActivity.actorType}</span>
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500 mb-1 font-medium">Target</p>
                              <p className="font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded inline-block border border-gray-200">{selectedActivity.targetIdent || selectedActivity.targetId}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 mb-1 font-medium">Time</p>
                              <p className="text-gray-900">{new Date(selectedActivity.createdAt).toLocaleString()}</p>
                            </div>
                            {selectedActivity.message && selectedActivity.message !== `${selectedActivity.targetType} ${selectedActivity.action}d` && (
                              <div className="col-span-2">
                                <p className="text-gray-500 mb-1 font-medium">Message</p>
                                <p className="text-gray-700 italic border-l-2 border-gray-300 pl-2">{selectedActivity.message}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                          <button
                            onClick={() => {
                              deleteActivity(selectedActivity._id);
                              setSelectedActivity(null);
                            }}
                            className="px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 rounded-md font-medium text-sm transition"
                          >
                            Delete Activity
                          </button>
                          <button onClick={() => setSelectedActivity(null)} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium text-sm">
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SuperAdmin;
